/**
 * Istanza di i18next della dashboard.
 *
 * Tre scelte, e il loro perché:
 *
 *  - **risorse in bundle, mai dalla rete.** Nessun `i18next-http-backend`: i
 *    testi sono codice, arrivano con il codice e non dipendono da un file che
 *    un proxy può servire sbagliato. L'italiano sta nel chunk iniziale, e
 *    l'inglese in un `import()` a parte, così una lingua in più non pesa sulla
 *    prima pagina di chi non la usa;
 *  - **un'istanza esplicita** (`createInstance`) invece del singleton globale:
 *    è quella che i test possono ispezionare e riportare alla lingua voluta;
 *  - **un solo spazio dei nomi** (`ui`): la struttura per area sta nelle
 *    chiavi (`projects.list.empty`), che si leggono senza sapere com'è
 *    organizzato il caricamento.
 *
 * L'augmentation di `CustomTypeOptions` in fondo tipizza ogni `t()` e ogni
 * `Trans` dell'applicazione sulle chiavi italiane: una chiave sbagliata non
 * arriva a schermo come testo grezzo, non compila.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import type { Locale as AntdLocale } from 'antd/es/locale';

import { registerAntdLocale } from './antdLocale';
import { it, type Translations } from './it';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  initialLanguage,
  isLanguage,
  storeLanguage,
  type Language,
} from './language';

/** Unico spazio dei nomi: la struttura per area sta nelle chiavi. */
export const NAMESPACE = 'ui';

/** Istanza di i18next usata da tutta l'applicazione e da tutti i test. */
export const i18n = i18next.createInstance();

// L'inizializzazione è sincrona: le risorse sono già in memoria e non c'è
// backend da interrogare, quindi al primo render `t()` restituisce già i testi
// invece delle chiavi. `initReactI18next` registra questa istanza come quella
// predefinita di react-i18next, così `useTranslation()` la trova ovunque.
void i18n.use(initReactI18next).init({
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...LANGUAGES],
  ns: [NAMESPACE],
  defaultNS: NAMESPACE,
  resources: { [DEFAULT_LANGUAGE]: { [NAMESPACE]: it } },
  // React sfugge già il contenuto che rende: la doppia sfuggitura
  // trasformerebbe un apostrofo in `&#39;` dentro il testo.
  interpolation: { escapeValue: false },
});

/**
 * Lingua attiva in questo momento.
 *
 * @returns La lingua attiva, o il default se i18next riferisce un tag che non
 *   conosciamo (non accade con `supportedLngs`, ma il tipo non lo sa).
 */
export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

/** Dizionario e locale di antd di una lingua caricata a richiesta. */
interface LoadedLanguage {
  dictionary: object;
  antdLocale: AntdLocale;
}

/**
 * Caricatori delle lingue diverse dall'italiano: un `import()` per lingua, quindi un chunk
 * per lingua, scaricato solo da chi la usa.
 */
const LOADERS: Partial<Record<Language, () => Promise<LoadedLanguage>>> = {
  en: async () => {
    const module = await import('./en');
    return { dictionary: module.en, antdLocale: module.antdLocale };
  },
  de: async () => {
    const module = await import('./de');
    return { dictionary: module.de, antdLocale: module.antdLocale };
  },
  es: async () => {
    const module = await import('./es');
    return { dictionary: module.es, antdLocale: module.antdLocale };
  },
  ru: async () => {
    const module = await import('./ru');
    return { dictionary: module.ru, antdLocale: module.antdLocale };
  },
  ko: async () => {
    const module = await import('./ko');
    return { dictionary: module.ko, antdLocale: module.antdLocale };
  },
  th: async () => {
    const module = await import('./th');
    return { dictionary: module.th, antdLocale: module.antdLocale };
  },
};

/**
 * Assicura che le risorse di una lingua siano caricate.
 *
 * L'italiano è già in memoria; ogni altra lingua arriva con un `import()`, cioè con
 * un chunk separato. Chiamarla due volte non scarica due volte: il bundler
 * memorizza il modulo e `hasResourceBundle` evita il secondo inserimento.
 *
 * @param language Lingua da preparare.
 * @returns Una promessa che si risolve quando la lingua è utilizzabile.
 */
export async function ensureLanguage(language: Language): Promise<void> {
  if (i18n.hasResourceBundle(language, NAMESPACE)) {
    return;
  }
  const loader = LOADERS[language];
  if (loader === undefined) {
    return;
  }
  const { dictionary, antdLocale } = await loader();
  i18n.addResourceBundle(language, NAMESPACE, dictionary, true, true);
  registerAntdLocale(language, antdLocale);
}

/**
 * Passa a una lingua e la ricorda per le visite successive.
 *
 * È il comando del selettore: carica le risorse se mancano, cambia la lingua
 * attiva (react-i18next ridisegna ciò che dipende da `t`) e salva la
 * preferenza. Il salvataggio è l'ultimo passo: se fallisce, la lingua è
 * comunque cambiata.
 *
 * @param language Lingua scelta dall'utente.
 * @returns Una promessa che si risolve a cambio avvenuto.
 */
export async function selectLanguage(language: Language): Promise<void> {
  await ensureLanguage(language);
  await i18n.changeLanguage(language);
  storeLanguage(language);
}

/**
 * Traduce una chiave fuori da React.
 *
 * Serve ai moduli che producono testo senza essere componenti — il client
 * HTTP, la traduzione degli errori, il contesto di autenticazione — e che non
 * possono usare `useTranslation`. È la stessa istanza, quindi la lingua è
 * sempre quella attiva.
 */
export const t = i18n.t.bind(i18n);

declare module 'i18next' {
  /** Tipi delle chiavi di traduzione, ricavati dal dizionario italiano. */
  interface CustomTypeOptions {
    /** Spazio dei nomi predefinito: si scrive `t('projects.list.title')`. */
    defaultNS: typeof NAMESPACE;
    /** Le risorse disponibili, cioè la forma del dizionario italiano. */
    resources: { ui: Translations };
  }
}

export { DEFAULT_LANGUAGE, LANGUAGES, intlLocale, type Language } from './language';
